import { useState, useEffect } from 'react'
import { User, Mail, MapPin, Calendar, Shield, Key, Save, Edit3, Eye, EyeOff, Settings, CreditCard, Check, X, Clock } from 'lucide-react'
import { validateAccessToken, getUserIdFromToken, getTokenExpiration, isTokenExpired } from '../../utils/tokenUtils'
import subscriptionService from '../../services/subscriptionService'
import { getSubscriptionPlans } from '../../services/subscription_plan'

const Profile = () => {
  const [userProfile, setUserProfile] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showToken, setShowToken] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [accessToken, setAccessToken] = useState('')
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [subscriptionPlans, setSubscriptionPlans] = useState([])
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true)

  // Load user profile from localStorage or API
  useEffect(() => {
    loadUserProfile()
    loadSubscriptionStatus()
    loadSubscriptionPlans()
  }, [])

  const loadUserProfile = async () => {
    try {
      setIsLoading(true)

      // Get access token from localStorage or context
      const token = localStorage.getItem('accessToken') || ''
      setAccessToken(token)

      if (token) {
        const tokenValidation = validateAccessToken(token)

        if (tokenValidation.isValid && !tokenValidation.isExpired) {
          const userId = getUserIdFromToken(token)
          const expiration = getTokenExpiration(token)

          // Load profile data (in real app, this would come from API)
          const profileData = {
            userId,
            email: 'user@example.com', // This would come from your API
            name: 'John Doe', // This would come from your API
            location: 'New York, NY', // This would come from your API
            joinDate: new Date('2024-01-15').toISOString(),
            tokenExpiration: expiration,
            isActive: true,
            avatar: null,
            preferences: {
              theme: 'dark',
              notifications: true,
              language: 'en'
            }
          }

          setUserProfile(profileData)
        } else {
          console.error('Invalid or expired token')
          setUserProfile(null)
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateProfile = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const updatePreferences = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [field]: value
      }
    }))
  }

  const saveProfile = async () => {
    try {
      setSaveStatus('saving')

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Here you would typically send the profile data to your API
      console.log('Saving profile:', userProfile)

      setSaveStatus('success')
      setIsEditing(false)

      // Clear success status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Error saving profile:', error)
      setSaveStatus('error')

      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(null), 5000)
    }
  }

  const refreshToken = async () => {
    try {
      // Simulate token refresh
      await new Promise(resolve => setTimeout(resolve, 1000))

      // In real app, call your refresh token API
      console.log('Refreshing token...')

      // Update token expiration
      const newExpiration = new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours from now
      setUserProfile(prev => ({
        ...prev,
        tokenExpiration: newExpiration
      }))
    } catch (error) {
      console.error('Error refreshing token:', error)
    }
  }

  const loadSubscriptionStatus = async () => {
    try {
      setIsLoadingSubscription(true)
      const response = await subscriptionService.checkSubscription()

      // Handle both response formats:
      // Active: { error: false, msg: "Active subscription found", data: {...} }
      // Expired: { detail: { error: true, msg: "Subscription expired", data: null } }
      if (response.detail && response.detail.error) {
        // Subscription expired
        setSubscriptionStatus({
          isActive: false,
          isExpired: true,
          message: response.detail.msg || 'Subscription expired'
        })
      } else if (!response.error && response.data) {
        // Active subscription
        setSubscriptionStatus({
          isActive: true,
          isExpired: false,
          message: response.msg || 'Active subscription found',
          data: response.data
        })
      } else {
        // No subscription
        setSubscriptionStatus({
          isActive: false,
          isExpired: false,
          message: 'No subscription found'
        })
      }
    } catch (error) {
      console.error('Error loading subscription status:', error)
      // Handle error response format
      if (error.response?.data?.detail?.error) {
        setSubscriptionStatus({
          isActive: false,
          isExpired: true,
          message: error.response.data.detail.msg || 'Subscription expired'
        })
      } else {
        setSubscriptionStatus({
          isActive: false,
          isExpired: false,
          message: 'Unable to load subscription status'
        })
      }
    } finally {
      setIsLoadingSubscription(false)
    }
  }

  const loadSubscriptionPlans = async () => {
    try {
      const response = await getSubscriptionPlans()
      if (response.data && Array.isArray(response.data)) {
        // Sort plans: FREE first, then by price
        const sortedPlans = response.data.sort((a, b) => {
          if (a.name === 'FREE') return -1
          if (b.name === 'FREE') return 1
          return a.price - b.price
        })
        setSubscriptionPlans(sortedPlans)
      }
    } catch (error) {
      console.error('Error loading subscription plans:', error)
    }
  }

  const getTokenStatus = () => {
    if (!accessToken) return { status: 'none', color: 'text-gray-400' }

    const isExpired = isTokenExpired(accessToken)
    if (isExpired) return { status: 'expired', color: 'text-red-400' }

    const expiration = getTokenExpiration(accessToken)
    const hoursUntilExpiry = Math.floor((expiration - new Date()) / (1000 * 60 * 60))

    if (hoursUntilExpiry < 2) return { status: 'expiring', color: 'text-yellow-400' }
    return { status: 'valid', color: 'text-green-400' }
  }

  const tokenStatus = getTokenStatus()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="ai-loading mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold ai-text-gradient mb-2">Loading Profile</h2>
          <p className="text-secondary-400">Please wait...</p>
        </div>
      </div>
    )
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
          <p className="text-secondary-400 mb-4">Unable to load your profile information.</p>
          <button
            onClick={loadUserProfile}
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className=" mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Profile Management</h1>
          <p className="text-secondary-400">Manage your account settings and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Basic Information
                </h2>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={userProfile.email}
                    onChange={(e) => updateProfile('email', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={userProfile.name}
                    onChange={(e) => updateProfile('name', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={userProfile.location}
                    onChange={(e) => updateProfile('location', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Member Since
                  </label>
                  <input
                    type="text"
                    value={new Date(userProfile.joinDate).toLocaleDateString()}
                    disabled
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-gray-400 cursor-not-allowed"
                  />
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={saveProfile}
                    disabled={saveStatus === 'saving'}
                    className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                  </button>

                  {saveStatus === 'success' && (
                    <span className="flex items-center text-green-400">
                      ✓ Changes saved successfully
                    </span>
                  )}

                  {saveStatus === 'error' && (
                    <span className="flex items-center text-red-400">
                      ✗ Failed to save changes
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Preferences */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Preferences
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Theme
                  </label>
                  <select
                    value={userProfile.preferences.theme}
                    onChange={(e) => updatePreferences('theme', e.target.value)}
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Email Notifications
                  </label>
                  <input
                    type="checkbox"
                    checked={userProfile.preferences.notifications}
                    onChange={(e) => updatePreferences('notifications', e.target.checked)}
                    className="w-4 h-4 text-primary-500 bg-secondary-700 border-secondary-600 rounded focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Language
                  </label>
                  <select
                    value={userProfile.preferences.language}
                    onChange={(e) => updatePreferences('language', e.target.value)}
                    className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Token Information */}
          <div className="space-y-6">
            {/* Account Status */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Account Status
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Account Status</span>
                  <span className={`px-2 py-1 rounded text-sm ${userProfile.isActive
                    ? 'bg-green-900 text-green-300'
                    : 'bg-red-900 text-red-300'
                    }`}>
                    {userProfile.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Member Since</span>
                  <span className="text-gray-300">
                    {new Date(userProfile.joinDate).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Last Updated</span>
                  <span className="text-gray-300">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Subscription
              </h2>

              {isLoadingSubscription ? (
                <div className="text-center py-4">
                  <div className="text-secondary-400">Loading subscription status...</div>
                </div>
              ) : subscriptionStatus ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Status</span>
                    <span className={`px-3 py-1 rounded text-sm font-medium flex items-center space-x-1 ${subscriptionStatus.isActive
                      ? 'bg-green-900 text-green-300'
                      : subscriptionStatus.isExpired
                        ? 'bg-red-900 text-red-300'
                        : 'bg-yellow-900 text-yellow-300'
                      }`}>
                      {subscriptionStatus.isActive ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Active</span>
                        </>
                      ) : subscriptionStatus.isExpired ? (
                        <>
                          <X className="w-4 h-4" />
                          <span>Expired</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>No Subscription</span>
                        </>
                      )}
                    </span>
                  </div>

                  {subscriptionStatus.data && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">Plan Type</span>
                        <span className="text-white font-medium">
                          {subscriptionStatus.data.subscription_type || 'N/A'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">Expires At</span>
                        <span className="text-white">
                          {subscriptionStatus.data.expires_at
                            ? new Date(subscriptionStatus.data.expires_at).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">Created At</span>
                        <span className="text-white">
                          {subscriptionStatus.data.created_at
                            ? new Date(subscriptionStatus.data.created_at).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                    </>
                  )}

                  {subscriptionStatus.message && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${subscriptionStatus.isActive
                      ? 'bg-green-900/30 text-green-300'
                      : subscriptionStatus.isExpired
                        ? 'bg-red-900/30 text-red-300'
                        : 'bg-yellow-900/30 text-yellow-300'
                      }`}>
                      {subscriptionStatus.message}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-secondary-400">
                  Unable to load subscription status
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        {subscriptionPlans.length > 0 && (
          <div className="mt-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Available Plans</h2>
              <p className="text-secondary-400">Choose a subscription plan that fits your needs</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {subscriptionPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-secondary-800 rounded-lg p-6 border-2 transition-all flex flex-col ${plan.name === 'FREE'
                    ? 'border-primary-500'
                    : subscriptionStatus?.data?.subscription_type === plan.name
                      ? 'border-green-500'
                      : 'border-secondary-600 hover:border-primary-500'
                    }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                    {subscriptionStatus?.data?.subscription_type === plan.name && (
                      <span className="px-2 py-1 bg-green-900 text-green-300 text-xs rounded">
                        Current
                      </span>
                    )}
                  </div>

                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">
                      ₹{plan.price === 0 ? '0' : plan.price.toLocaleString()}
                    </span>
                    <span className="text-secondary-400 ml-2">
                      / {plan.duration_days} days
                    </span>
                  </div>

                  <p className="text-secondary-400 text-sm mb-4">{plan.description}</p>

                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-300">
                        <Check className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors mt-auto ${plan.name === 'FREE'
                      ? 'bg-primary-500 hover:bg-primary-600 text-white'
                      : subscriptionStatus?.data?.subscription_type === plan.name
                        ? 'bg-secondary-700 text-gray-400 cursor-not-allowed'
                        : 'bg-primary-500 hover:bg-primary-600 text-white'
                      }`}
                    disabled={subscriptionStatus?.data?.subscription_type === plan.name}
                  >
                    {subscriptionStatus?.data?.subscription_type === plan.name
                      ? 'Current Plan'
                      : plan.price === 0
                        ? 'Get Started'
                        : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile
